/* eslint-disable @trello/enforce-variable-case */
import type {
  FCPAttribution,
  // CLSMetric,
  FCPMetricWithAttribution,
  FIDAttribution,
  FIDMetricWithAttribution,
  LCPAttribution,
  LCPMetricWithAttribution,
  MetricWithAttribution,
  TTFBAttribution,
  TTFBMetricWithAttribution,
} from 'web-vitals/attribution';
import {
  /* onCLS, */ onFCP,
  onFID,
  onLCP,
  onTTFB,
} from 'web-vitals/attribution';

import type { SourceType, Task } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { Cookies } from '@trello/cookies';
import { dangerouslyGetDynamicConfigSync } from '@trello/dynamic-config/dangerouslyGetDynamicConfigSync';
import { dangerouslyGetFeatureGateSync } from '@trello/feature-gate-client';
import { getScreenFromUrl } from '@trello/marketing-screens';

// Mapping for screen -> taskName for the VitalStats web-vitals tasks
const sourceToTask: Partial<Record<SourceType, Task | undefined>> = {
  boardScreen: 'load-page/boardScreen',
  cardDetailScreen: 'load-page/cardDetailScreen',
  memberHomeScreen: 'load-page/memberHomeScreen',
};

const QUICKLOAD_CONFIG_KEY = 'trello_web_native_current_board_info';
const SUBSCRIPTIONS_GATE = 'gql_client_subscriptions';

function isValidScreenForTask(task: Task | undefined): task is Task {
  return task !== undefined;
}

const isFCPAttribution = (
  name: MetricWithAttribution['name'],
  attribution:
    | FCPAttribution
    | FIDAttribution
    | LCPAttribution
    | TTFBAttribution,
): attribution is FCPAttribution => {
  return name === 'FCP';
};

const isFIDAttribution = (
  name: MetricWithAttribution['name'],
  attribution:
    | FCPAttribution
    | FIDAttribution
    | LCPAttribution
    | TTFBAttribution,
): attribution is FIDAttribution => {
  return name === 'FID';
};

const isLCPAttribution = (
  name: MetricWithAttribution['name'],
  attribution:
    | FCPAttribution
    | FIDAttribution
    | LCPAttribution
    | TTFBAttribution,
): attribution is LCPAttribution => {
  return name === 'LCP';
};

const isTTFBAttribution = (
  name: MetricWithAttribution['name'],
  attribution:
    | FCPAttribution
    | FIDAttribution
    | LCPAttribution
    | TTFBAttribution,
): attribution is TTFBAttribution => {
  return name === 'TTFB';
};

const formatAttribution = (
  name: MetricWithAttribution['name'],
  attribution:
    | FCPAttribution
    | FIDAttribution
    | LCPAttribution
    | TTFBAttribution,
) => {
  if (isFCPAttribution(name, attribution)) {
    return {
      firstByteToFCP: attribution.firstByteToFCP,
      timeToFirstByte: attribution.timeToFirstByte,
    };
  } else if (isFIDAttribution(name, attribution)) {
    return {
      eventTarget: attribution.eventTarget,
      eventTime: attribution.eventTime,
      eventType: attribution.eventType,
    };
  } else if (isLCPAttribution(name, attribution)) {
    return {
      datasetTestId: (attribution.lcpEntry?.element as HTMLElement)?.dataset
        ?.testid,
      element: attribution.element,
      elementRenderDelay: attribution.elementRenderDelay,
      resourceLoadDelay: attribution.resourceLoadDelay,
      resourceLoadDuration: attribution.resourceLoadDuration,
      timeToFirstByte: attribution.timeToFirstByte,
    };
  } else if (isTTFBAttribution(name, attribution)) {
    return {
      connectionDuration: attribution.connectionDuration,
      dnsDuration: attribution.dnsDuration,
      requestDuration: attribution.requestDuration,
      waitingDuration: attribution.waitingDuration,
    };
  }
};

const formatMetric = (
  metric:
    | FCPMetricWithAttribution
    | FIDMetricWithAttribution
    | LCPMetricWithAttribution
    | TTFBMetricWithAttribution,
) => ({
  value: metric.value,
  rating: metric.rating,
  navigationType: metric.navigationType,
  attribution: formatAttribution(metric.name, metric.attribution),
});

let hasSentAnalytic = false;

/**
 * Reports web vitals metrics to analytics.
 * The web-vitals library uses the buffered flag for PerformanceObserver,
 * allowing it to access performance entries that occurred before the library was loaded.
 * This means you do not need to load this library early in order to get accurate performance data.
 * In general, this library should be deferred until after other user-impacting code has loaded.
 */
export const reportWebVitals = (options?: {
  meta: { [key: string]: boolean | number | string | null | undefined };
}) => {
  // https://web.dev/cls/
  // CLS is a measure of the largest burst of layout shift scores
  // for every unexpected layout shift that occurs during the entire lifespan of a page.
  // let cummulativeLayoutShift: CLSMetric;

  // https://web.dev/fid/
  // FID measures the time from when a user first interacts with a page (that is, when they
  // click a link, tap on a button, or use a custom, JavaScript-powered control) to the
  // time when the browser is actually able to begin processing event handlers in response to that interaction
  let firstInputDelay: FIDMetricWithAttribution;

  // https://web.dev/lcp/
  // The Largest Contentful Paint (LCP) metric reports the render time of the largest image
  // or text block visible within the viewport, relative to when the page first started loading.
  let largestContentfulPaint: LCPMetricWithAttribution;

  // https://web.dev/fcp/
  // The First Contentful Paint (FCP) metric measures the time from when the page starts loading
  // to when any part of the page's content is rendered on the screen. For this metric, "content"
  // refers to text, images (including background images), <svg> elements, or non-white <canvas> elements.
  let firstContentfulPaint: FCPMetricWithAttribution;

  // https://web.dev/ttfb/
  // TTFB is a metric that measures the time between the request for a resource and when the
  // first byte of a response begins to arrive.
  let timeToFirstByte: TTFBMetricWithAttribution;

  let longTasks = 0;
  let totalLongTaskDuration = 0;
  let observer: PerformanceObserver | undefined;

  const hasAllData = () =>
    !!firstInputDelay &&
    !!largestContentfulPaint &&
    !!firstContentfulPaint &&
    !!timeToFirstByte;

  const sendAnalyticIfAllDataIsPresent = () => {
    if (hasSentAnalytic) {
      return;
    }

    if (!hasAllData()) {
      return;
    }

    if (Cookies.get('display_web_vitals_in_console')) {
      // eslint-disable-next-line no-console
      console.log('Web Vitals: ', {
        longTasks,
        totalLongTaskDuration,
        firstInputDelay: formatMetric(firstInputDelay),
        largestContentfulPaint: formatMetric(largestContentfulPaint),
        firstContentfulPaint: formatMetric(firstContentfulPaint),
        timeToFirstByte: formatMetric(timeToFirstByte),
      });
    }

    const source = getScreenFromUrl();
    hasSentAnalytic = true;
    observer?.disconnect();
    Analytics.sendOperationalEvent({
      action: 'evaluated',
      actionSubject: 'web-vitals',
      source,
      attributes: {
        meta: options?.meta,
        longTasks,
        totalLongTaskDuration,
        firstInputDelay: formatMetric(firstInputDelay),
        largestContentfulPaint: formatMetric(largestContentfulPaint),
        firstContentfulPaint: formatMetric(firstContentfulPaint),
        timeToFirstByte: formatMetric(timeToFirstByte),
        flags: {
          [QUICKLOAD_CONFIG_KEY]:
            dangerouslyGetDynamicConfigSync(QUICKLOAD_CONFIG_KEY),
          [SUBSCRIPTIONS_GATE]:
            dangerouslyGetFeatureGateSync(SUBSCRIPTIONS_GATE),
        },
      },
    });

    // Instrument web-vitals as a capability to unlock VitalStats tooling
    const taskName = sourceToTask[source];
    if (isValidScreenForTask(taskName)) {
      const taskData = {
        taskName,
        source,
      };

      // Send a start and success with the LCP duration for the performance measurement
      Analytics.taskSucceeded({
        ...taskData,
        traceId: Analytics.startTask(taskData),
        attributes: {
          duration: largestContentfulPaint.value,
          largestContentfulPaint: formatMetric(largestContentfulPaint),
        },
      });
    }
  };

  try {
    observer = new window.PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        longTasks++;
        totalLongTaskDuration += entry.duration;
      });
    });

    observer.observe({ type: 'longtask', buffered: true });

    onFID((data) => {
      firstInputDelay = data;
      sendAnalyticIfAllDataIsPresent();
    });
    onLCP((data) => {
      largestContentfulPaint = data;
      sendAnalyticIfAllDataIsPresent();
    });
    onTTFB((data) => {
      timeToFirstByte = data;
      sendAnalyticIfAllDataIsPresent();
    });
    onFCP((data) => {
      firstContentfulPaint = data;
      sendAnalyticIfAllDataIsPresent();
    });
  } catch (err) {
    // no browser support probably. Ignore
  }
};
